import { Directive, forwardRef, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, Observable } from 'rxjs';

type OnChangeFn<T> = (value: T) => void;
type OnTouchedFn = () => void;

@Directive({
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ValueAccessorDirective),
      multi: true,
    },
  ],
})
export class ValueAccessorDirective<T = unknown> implements ControlValueAccessor, OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChangeCallback: OnChangeFn<T> = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouchedCallback: OnTouchedFn = () => {};
  private readonly valueSubject = new Subject<T>();
  private readonly disabledSubject = new Subject<boolean>();

  public readonly value$: Observable<T> = this.valueSubject.asObservable();
  public readonly disabled$: Observable<boolean> = this.disabledSubject.asObservable();

  ngOnDestroy(): void {
    this.valueSubject.complete();
    this.disabledSubject.complete();
  }

  public notifyValueChange(value: T): void {
    this.onChangeCallback(value);
  }

  public notifyTouched(): void {
    this.onTouchedCallback();
  }

  writeValue(value: T): void {
    this.valueSubject.next(value);
  }

  registerOnChange(fn: OnChangeFn<T>): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: OnTouchedFn): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledSubject.next(isDisabled);
  }
}
